import { createIntl, createIntlCache } from '@formatjs/intl';
import { StringMap } from '../types/types';
// eslint-disable-next-line import/no-relative-packages
import DefaultI18nMessages from '../../../i18n/en-US.json';
// eslint-disable-next-line import/no-relative-packages
import i18nConfig from '../../../i18n/i18n.config.json';

interface I18nConfig {
  languageFileVersion: string;
}

export type MessageDescriptor = {
  id: string;
  defaultMessage?: string;
};

export const DefaultLocale = 'en-US';

export function validateConfig(config: unknown): asserts config is I18nConfig {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    throw new Error('Invalid i18n config: config must be an object');
  }

  if (!('languageFileVersion' in config)) {
    throw new Error('Invalid i18n config: languageFileVersion is required');
  }

  if (typeof (config as I18nConfig).languageFileVersion !== 'string') {
    throw new Error('Invalid i18n config: languageFileVersion must be a string');
  }
}

export function getLanguageFileVersion() {
  let languageFileVersion: string;
  try {
    validateConfig(i18nConfig);
    languageFileVersion = i18nConfig.languageFileVersion;
  } catch (error) {
    console.error('Error loading language file version: ', error);
  }
  return languageFileVersion;
}

export const languageFileVersion = getLanguageFileVersion();

export function getLocale(): string {
  if (window.AvaAppUser?.locale) {
    return window.AvaAppUser.locale;
  }
  if (window.aui?.locale) {
    return window.aui.locale;
  }
  return DefaultLocale;
}

function getBaseAssetsUrl() {
  switch (window?.aui?.env) {
    case 'local':
    case 'ci':
    case 'qa':
      return 'https://assets.avalara.io';
    default:
      return 'https://assets.avalara.com';
  }
}

const baseAssetsUrl = getBaseAssetsUrl();
export const skylabL10nUrl = `${baseAssetsUrl}/skylab-sdk/locales/${languageFileVersion}/`;

const dataserviceL10nUrl = `${baseAssetsUrl}/dataservice/locales/`;

export async function fetchLangFiles(locale) {
  const allFetches = [];
  const allJson = [];
  const failedFiles = [];
  let mergedJson = {};

  const fetchSingleLangFile = async (name: string, url: string) => {
    try {
      const response = await fetch(`${url}${locale}.json`);
      if (response.ok) {
        const json = await response.json();
        allJson.push(json);
      } else {
        failedFiles.push({
          url: response.url,
          httpStatusCode: response.status,
          httpStatusText: response.statusText,
        });
      }
    } catch (e) {
      console.error(`Error fetching ${name} language file`, e);
    }
  };

  allFetches.push(fetchSingleLangFile('Skylab', skylabL10nUrl));
  allFetches.push(fetchSingleLangFile('Dataservice', dataserviceL10nUrl));

  // wait for all the fetches to finish
  await Promise.all(allFetches);

  // combine all language files into a single object
  mergedJson = allJson.reduce((acc, json) => {
    return { ...acc, ...json };
  }, {});

  return mergedJson;
}

// exported to make testing easier
export function getDefaultMessages() {
  return DefaultI18nMessages;
}

// create intl object
export async function createIntlObject(locale: string, newMessages: StringMap | Record<string, string> = {}) {
  const cache = createIntlCache();
  const defaultMessages = getDefaultMessages();
  const newMessagesHasKeys = Object.keys(newMessages).length;

  let messages = newMessagesHasKeys ? newMessages : defaultMessages;

  if (!newMessagesHasKeys) {
    messages = await fetchLangFiles(locale);
  }

  const intl = createIntl(
    {
      locale,
      messages,
      onError: err => {
        if (err.code === 'MISSING_TRANSLATION') {
          return; // these error logs were causing a lot of noise in unit tests
        }
        throw err;
      },
    },
    cache,
  );

  const intlEvent = new CustomEvent('s-intlupdate', {
    bubbles: true,
    detail: { shouldUpdate: true },
  });
  window.dispatchEvent(intlEvent);

  const existingAui = window.aui || {};
  window.aui = {
    ...existingAui,
    intlInitializing: false,
    intl,
  };

  return intl;
}

// TODO: remove when support for i18n attributes/properties is removed
export function replaceTemplateStringsWithValues(message?: string | null | undefined, values: Record<string, string | number> = {}) {
  if (!message) return '';
  const keys = Object.keys(values);
  if (!keys.length) return message;
  let newMessage = message;
  for (const key of keys) {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    newMessage = newMessage.replace(regex, values[key].toString() || '');
  }
  return newMessage;
}
